"use client";

import NextLink from 'next/link';
import { useLoadingState } from '@/hooks/useLoadingState';
import type { LinkProps as NextLinkProps } from 'next/link';
import { useSWRConfig } from 'swr';
import { directusFetcher, getCacheKey } from '@/hooks/useDirectus';

interface ClientLinkProps extends NextLinkProps {
  children: React.ReactNode;
  className?: string;
  target?: string;
  onClick?: () => void;
  onMouseEnter?: () => void;
  prefetch?: boolean;
}

export default function Link({
	children,
	onClick,
	onMouseEnter,
	target,
	prefetch,
	...props
}: ClientLinkProps) {
  const { setIsLoading } = useLoadingState();
	const { mutate } = useSWRConfig();
  

  const handleClick = () => {
    setIsLoading(true);
    if (onClick) onClick();
    
  };

	const handleMouseEnter = async () => {
		if (onMouseEnter) onMouseEnter();

		const href =
			typeof props.href === 'string' ? props.href : props.href?.pathname || '';

		if (
			!href ||
			href.startsWith('http') ||
			href.startsWith('mailto') ||
			href.startsWith('tel')
		) {
			return;
		}

		const slug = href.replace(/^\/+|\/+$/g, '') || 'home';
		const gallerySlug = process.env.NEXT_PUBLIC_GALLERY_SLUG;

		if (gallerySlug && (slug === gallerySlug || slug.startsWith(`${gallerySlug}/`))) {
			return;
		}

		const pageKey = getCacheKey.page(slug);
		if (!pageKey) return;

		try {
			await mutate(
				pageKey,
				() => directusFetcher(pageKey),
				{ populateCache: true, revalidate: false }
			);

			if (href.includes('/blog/')) {
				const blogKey = getCacheKey.blogPosts(1, 6);
				await mutate(
					blogKey,
					() => directusFetcher(blogKey),
					{ populateCache: true, revalidate: false }
				);
			}
		} catch (error) {
			console.debug('Preload failed for:', href, error);
		}
	};

  return (
    <NextLink
			{...props}
			onClick={handleClick}
			onMouseEnter={handleMouseEnter}
			prefetch={prefetch}
		>
      {children}
    </NextLink>
  );
} 